import { find, isNil, pipe, not, reject, propEq } from 'ramda';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { LOG_OUT_EVENT } from '@app/data-services/session.service';
import { DebtService } from '@modules/member-state/services/debt.service';

const DEBT_KEYS = 'IS:SELECTED_DEBTS';

export interface Commission {
    percentage: number;
    description: string;
    debt: {
        serviceId: number;
        transactionId: string;
        description: string;
        amount: number;
    };
}

@Injectable({
    providedIn: 'root',
})
export class PaymentService {
    private _paymentList$: BehaviorSubject<Debt[]> = new BehaviorSubject<Debt[]>(
        sessionStorage.getItem(DEBT_KEYS) ? JSON.parse(sessionStorage.getItem(DEBT_KEYS)) : []
    );

    public paymentList$ = this._paymentList$.asObservable().pipe(
        tap((payDebts) => {
            sessionStorage.setItem(DEBT_KEYS, JSON.stringify(payDebts));
        }),
        shareReplay(1)
    );

    public commission$: Observable<{ commission: Commission; totalWithCommission: number }> = combineLatest([
        this.paymentList$,
        this.debtService.getCommission(),
    ]).pipe(
        map(([payDebts, commissionResponse]) => {
            if (!commissionResponse) {
                return null;
            }

            const total = payDebts.reduce((prev, debt) => prev + debt.amount, 0);
            const commissionAmount = (commissionResponse.value / 100) * total;

            const commission = {
                percentage: commissionResponse.value,
                description: commissionResponse.description,
                debt: {
                    serviceId: commissionResponse.service_id,
                    description: commissionResponse.name,
                    transactionId: commissionResponse.service_id,
                    amount: commissionAmount,
                },
            } as Commission;

            return {
                commission,
                totalWithCommission: total + commissionAmount,
            };
        }),
        shareReplay(1)
    );

    constructor(private debtService: DebtService) {
        window.addEventListener(LOG_OUT_EVENT, () => {
            this._paymentList$.next([]);
            sessionStorage.removeItem(DEBT_KEYS);
        });
    }

    revalidateDebts(): Observable<Debt[]> {
        return this.debtService.getDebts().pipe(
            map((debts: Debt[]) => {
                const validatedList = this._paymentList$.value.filter((paymentDebt) =>
                    debts.some((debt) => debt.id === paymentDebt.id)
                );
                this._paymentList$.next(validatedList);
                return debts;
            })
        );
    }

    addItem(debtItem: Debt) {
        if (this._paymentList$.value.some((debt) => debt.id === debtItem.id)) return;
        this._paymentList$.next([...this._paymentList$.value, debtItem]);
    }

    removeItem(item: Debt) {
        this._paymentList$.next(reject(propEq('id', item.id), this._paymentList$.value));
    }

    selectAll(items: Debt[]) {
        this._paymentList$.next([...items]);
    }

    removeAllItems() {
        this._paymentList$.next([]);
    }
}
